import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NEVER, type Observable, throwError } from 'rxjs';
import { APIError } from '../errors.constants';
import { InformationProvider } from '../information/information.provider';

export interface APIErrorToastingOptions {
    errorsMap?: Record<string, string>;
    defaultError?: string;
}

export interface APIErrorObservableOptions {
    reThrow?: boolean;
}

export type APIErrorHandlingOptions = APIErrorToastingOptions & APIErrorObservableOptions;

/**
 * This could be used in an HTTP interceptor, but for that, we should remove all other toast error handling from the app.
 * With an interceptor, there would be no extra work when implementing a route, but we would lose the ability to pass arguments like reThrow and errorsMap.
 * Maybe one day in Rosa or nx, to allow both ways of handling errors ?
 */
@Injectable({
    providedIn: 'root'
})
export class ErrorHandlingProvider {
    private _informationProvider = inject(InformationProvider);

    static DEFAULT_ERROR_MESSAGES: Partial<Record<APIError, string>> = {
        [APIError.ALREADY_ACCEPTED]: 'API_ERRORS.ALREADY_ACCEPTED',
        [APIError.ALREADY_CREATED]: 'API_ERRORS.ALREADY_CREATED',
        [APIError.ALREADY_LINKED]: 'API_ERRORS.ALREADY_LINKED',
        [APIError.LINKED_WITH_RESOURCE]: 'API_ERRORS.LINKED_WITH_RESOURCE',
        [APIError.INVALID_GRANT]: 'ERRORS.LOGIN.FAILED_AUTH',
        [APIError.NOT_DEPLOYED]: 'API_ERRORS.NOT_DEPLOYED',
        [APIError.POINT_MUST_HAVE_DIFFERENT_SOURCES]: 'API_ERRORS.POINT_MUST_HAVE_DIFFERENT_SOURCES',
        [APIError.SYSTEM_LINKED_WITH_TILE]: 'API_ERRORS.SYSTEM_LINKED_WITH_TILE',
        [APIError.UNEXISTING_BUILDING]: 'API_ERRORS.UNEXISTING_BUILDING',
        [APIError.UNEXISTING_INVITATION]: 'API_ERRORS.UNEXISTING_INVITATION',
        [APIError.UNEXISTING_INVOICE]: 'API_ERRORS.UNEXISTING_INVOICE',
        [APIError.UNEXISTING_SENSOR]: 'API_ERRORS.UNEXISTING_SENSOR',
        [APIError.UNEXISTING_FILE]: 'API_ERRORS.UNEXISTING_FILE',
        [APIError.UNEXISTING_SYSTEM]: 'API_ERRORS.UNEXISTING_SYSTEM',
        [APIError.UNAUTHORIZED]: 'API_ERRORS.UNAUTHORIZED',
        [APIError.UNAUTHORIZED_FILE_TYPE]: 'API_ERRORS.UNAUTHORIZED_FILE_TYPE',
        [APIError.WRONG_UNIVERSE]: 'API_ERRORS.WRONG_UNIVERSE',
        [APIError.FILE_NAME_ALREADY_USED]: 'API_ERRORS.FILE_NAME_ALREADY_USED',
        [APIError.DUPLICATE_AXIS_CATEGORY_NAME]: 'API_ERRORS.DUPLICATE_AXIS_CATEGORY_NAME',
        [APIError.PASSWORD_MISMATCH]: 'API_ERRORS.PASSWORD_MISMATCH',
        [APIError.PASSWORDS_MUST_BE_DIFFERENTS]: 'API_ERRORS.PASSWORDS_MUST_BE_DIFFERENTS'
    };

    static DEFAULT_ERROR_MESSAGE: string = 'API_ERRORS.DEFAULT';

    handleAPIError$(apiError: HttpErrorResponse, options: APIErrorHandlingOptions = {}): Observable<never> {
        this.showToastForAPIError(apiError, options);
        return options.reThrow ? throwError(apiError) : NEVER;
    }

    showToastForAPIError(apiError: HttpErrorResponse, options: APIErrorToastingOptions = {}): void {
        this._informationProvider.showToast(this.getErrorMessageToTranslate(apiError, options), 'error');
    }

    private getErrorMessageToTranslate(apiError: HttpErrorResponse, options: APIErrorToastingOptions): string {
        for (const errorRecord of ErrorHandlingProvider.UNIVERSAL_ERRORS) {
            if (errorRecord.checkError(apiError)) {
                return errorRecord.message;
            }
        }

        const errorCode = this.getErrorCodeFromApiResponse(apiError);
        const errorMessage =
            options.errorsMap && options.errorsMap[errorCode]
                ? options.errorsMap[errorCode]
                : ErrorHandlingProvider.DEFAULT_ERROR_MESSAGES[errorCode]
                  ? ErrorHandlingProvider.DEFAULT_ERROR_MESSAGES[errorCode]
                  : options.defaultError
                    ? options.defaultError
                    : ErrorHandlingProvider.DEFAULT_ERROR_MESSAGE;

        return errorMessage;
    }

    getErrorCodeFromApiResponse(apiError: unknown): APIError {
        if (!apiError || !(apiError instanceof HttpErrorResponse)) return undefined!;
        if (!apiError.error) return undefined!;

        if (typeof apiError.error.error === 'string') {
            return apiError.error.error as APIError;
        }

        if (typeof apiError.error.message === 'string') {
            return apiError.error.message as APIError;
        }

        if (typeof apiError.error.error.errorCode === 'string') {
            return apiError.error.error.errorCode as APIError;
        }

        return undefined!;
    }

    getErrorPayloadFromApiResponse(apiError: unknown): any {
        if (!apiError || !(apiError instanceof HttpErrorResponse)) return;
        if (!apiError.error) return;
        if (!apiError.error.error) return;

        if (apiError.error.error.payload) {
            return apiError.error.error.payload;
        }
    }

    static UNIVERSAL_ERRORS: { checkError: (error: HttpErrorResponse) => boolean; message: string }[] = [
        {
            checkError: (apiError: HttpErrorResponse) => apiError.status === 0 && apiError.error instanceof ProgressEvent,
            message: 'ERRORS.NETWORK'
        },
        {
            checkError: (apiError: HttpErrorResponse) => apiError.status === 413,
            message: 'ERRORS.REQUEST_ENTITY_TOO_LARGE'
        }
    ];
}
